
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { DailyYardVehicle } from '@/models/DailyYardVehicle';
import { Vehicle } from '@/models/Vehicle.model';

// --- Services ---
import AppService from '@/services/app';
import DailyYardVehicleService from '@/services/dailyYardVehicle';
import JobCardService from '@/services/jobCard';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component
export default class DailyYardListListing extends Vue {
  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  // used by Add Vehicle Area
  selectedVehicle1: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  selectedVehicle2: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  activeVehiclesNotInYard: Vehicle[] = [];
  activeTrailersNotInYard: Vehicle[] = [];
  vehicleLoading: boolean = false;

  // used by Filter Area
  filterOptions: string[] = ['All', 'Service Due', 'Wheel Alignment Due', 'COF Due', 'License Due'];
  vehicleFilter: string = 'All';

  // used by No Data Slot
  isLoading: boolean = true;
  displayVehicles: DailyYardVehicle[] = [];

  // used by Item Dialog for Status Update
  showUpdateStatusDialog: boolean = false;  
  clickedItem: DailyYardVehicle = {
    id: 0,
    vehicle1Id: 0,
    vehicle1Code: '',
    vehicle1TypeId: 0,
    vehicle1Type: '',
    statusId: 0,
    status: '',
  };

  clicked: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Vehicle',
          value: 'vehicle1',
        },
        {
          text: 'Additional Trailer',
          value: 'vehicle2',
        },
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Comment',
          value: 'comment',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'vehicle1':
          if (sortDesc[0]) {
            items.sort((a: DailyYardVehicle, b: DailyYardVehicle) => a.vehicle1Code.toLowerCase().localeCompare(b.vehicle1Code.toLowerCase()));
          } else {
            items.sort((a: DailyYardVehicle, b: DailyYardVehicle) => b.vehicle1Code.toLowerCase().localeCompare(a.vehicle1Code.toLowerCase()));
          }
          break;

        case 'vehicle2':
          if (sortDesc[0]) {
            items.sort((a: DailyYardVehicle, b: DailyYardVehicle) => (a.vehicle2Code ? a.vehicle2Code : '').toLowerCase().localeCompare((b.vehicle2Code ? b.vehicle2Code : '').toLowerCase()));
          } else {
            items.sort((a: DailyYardVehicle, b: DailyYardVehicle) => (b.vehicle2Code ? b.vehicle2Code : '').toLowerCase().localeCompare((a.vehicle2Code ? a.vehicle2Code : '').toLowerCase()));
          }
          break;

        case 'status':
          if (sortDesc[0]) {
            items.sort((a: DailyYardVehicle, b: DailyYardVehicle) => a.status.toLowerCase().localeCompare(b.status.toLowerCase()));
          } else {
            items.sort((a: DailyYardVehicle, b: DailyYardVehicle) => b.status.toLowerCase().localeCompare(a.status.toLowerCase()));
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await AppService.listStatuses(false);
      await VehicleService.listTypes(false);
      await VehicleService.listActive(true);
      await JobCardService.listOpen(true);
      await DailyYardVehicleService.listAllYardStatuses(true);
      await this.listAll(true);

      this.updateActiveVechilesNotInYard();
      this.filterVehicles();
    }

    async listAll(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await DailyYardVehicleService.listAll(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async filterVehicles() {
      switch (this.vehicleFilter) {
        case ('All'):
          await this.listAll(false);
          this.displayVehicles = this.$store.getters['dailyYardVehicles/dailyYardVehicles'];
          break;

        case ('Service Due'):
          this.displayVehicles = this.$store.getters['dailyYardVehicles/dailyYardVehicles'].filter((item: DailyYardVehicle) => {
            // find vehicle 1 and check
            const vehicle1 = this.$store.getters['vehicles/vehicleById'](item.vehicle1Id);
            if (vehicle1 && vehicle1.odometer + 5000 > vehicle1.nextServiceOdo) {
              return true;
            }

            // find vehicle 2 and check
            if (item.vehicle2Id) {
              const vehicle2 = this.$store.getters['vehicles/vehicleById'](item.vehicle2Id);
              if (vehicle2 && vehicle2.odometer + 5000 > vehicle2.nextServiceOdo) {
                return true;
              }
            }
          });
          break;

        case ('Wheel Alignment Due'):
          this.displayVehicles = this.$store.getters['dailyYardVehicles/dailyYardVehicles'].filter((item: DailyYardVehicle) => {
            // find vehicle 1 and check
            const vehicle1 = this.$store.getters['vehicles/vehicleById'](item.vehicle1Id);
            if (vehicle1 && vehicle1.odometer + 5000 > vehicle1.nextWheelAlignmentOdo) {
              return true;
            }

            // find vehicle 2 and check
            if (item.vehicle2Id) {
              const vehicle2 = this.$store.getters['vehicles/vehicleById'](item.vehicle2Id);
              if (vehicle2 && vehicle2.odometer + 5000 > vehicle2.nextWheelAlignmentOdo) {
                return true;
              }
            }
          });
          break;

        case ('COF Due'):
          this.displayVehicles = this.$store.getters['dailyYardVehicles/dailyYardVehicles'].filter((item: DailyYardVehicle) => {
            // find vehicle 1 and check
            const vehicle1 = this.$store.getters['vehicles/vehicleById'](item.vehicle1Id);
            if (vehicle1) {
              const licenseEndDate = dayjs(vehicle1.licenseEndDate);
              const roadworthyDate = dayjs(vehicle1.roadworthyDate);
              // check if the license is due? (today is less than 60d until license date) 
              if (dayjs().isAfter(licenseEndDate.subtract(60, 'days'))) {
                // if license date is in the past
                if (dayjs().isAfter(licenseEndDate)) {
                  // then check if the roadworthy date is less than 60d until today
                  if (roadworthyDate.isBefore(dayjs().subtract(60, 'days'))) {
                    return true;
                  }
                } else {
                  // check if the roadworthy date is less than 60d until license date
                  if (roadworthyDate.isBefore(licenseEndDate.subtract(60, 'days'))) {
                    return true;
                  }
                }
              }
            }

            // find vehicle 2 and check
            if (item.vehicle2Id) {
              const vehicle2 = this.$store.getters['vehicles/vehicleById'](item.vehicle2Id);
              if (vehicle2) {
              const licenseEndDate = dayjs(vehicle2.licenseEndDate);
              const roadworthyDate = dayjs(vehicle2.roadworthyDate);
              // check if the license is due? (today is less than 60d until license date) 
              if (dayjs().isAfter(licenseEndDate.subtract(60, 'days'))) {
                // if license date is in the past
                if (dayjs().isAfter(licenseEndDate)) {
                  // then check if the roadworthy date is less than 60d until today
                  if (roadworthyDate.isBefore(dayjs().subtract(60, 'days'))) {
                    return true;
                  }
                } else {
                  // check if the roadworthy date is less than 60d until license date
                  if (roadworthyDate.isBefore(licenseEndDate.subtract(60, 'days'))) {
                    return true;
                  }
                }
              }
            }
            }
          });
          break;

        case ('License Due'):
          this.displayVehicles = this.$store.getters['dailyYardVehicles/dailyYardVehicles'].filter((item: DailyYardVehicle) => {
            // find vehicle 1 and check
            const vehicle1 = this.$store.getters['vehicles/vehicleById'](item.vehicle1Id);
            if (vehicle1 && dayjs().isAfter(dayjs(vehicle1.licenseEndDate).subtract(30, 'days'))) {
              return true;
            }

            // find vehicle 2 and check
            if (item.vehicle2Id) {
              const vehicle2 = this.$store.getters['vehicles/vehicleById'](item.vehicle2Id);
              if (vehicle2 && dayjs().isAfter(dayjs(vehicle2.licenseEndDate).subtract(30, 'days'))) {
                return true;
              }
            }
          });
          break;
      }
    }

    createJobCard(item: DailyYardVehicle) {
      this.$router.push({ name: 'Job Cards', params: {vehicle1: '' + this.hashids.encode(item.vehicle1Id), vehicle2: '' + this.hashids.encode(item.vehicle2Id) } } ); 
    }

    allowAddVehicleFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
      { type: 'Super User', level: 2},
      { type: 'Operator', level: 2},
      { type: 'Workshop User', level: 1},
      ]);
    }

    allowAddJobCardFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }
  // #endregion

  // #region Functions required for the Add Vehicle
    selectVehicle1(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedVehicle1 = selectedVehicle;
      } else {
        // likely was cleared so is now undefined
        this.selectedVehicle1 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
        this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      }

      this.updateActiveTrailersNotInYard();
    }

    selectVehicle2(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedVehicle2 = selectedVehicle;
      }
    }

    updateActiveVechilesNotInYard() {
      this.activeVehiclesNotInYard = this.$store.getters['vehicles/activeVehicles'].filter( (activeVehicle: Vehicle) =>         
        !this.$store.getters['dailyYardVehicles/dailyYardVehicles'].find((vehicle1: DailyYardVehicle) => vehicle1.vehicle1Id ===  activeVehicle.id) && 
        !this.$store.getters['dailyYardVehicles/dailyYardVehicles'].find((vehicle2: DailyYardVehicle) => vehicle2.vehicle2Id ===  activeVehicle.id)
      );
    }

    updateActiveTrailersNotInYard() {
      if (!this.selectedVehicle1) {
        this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      } else {
        this.activeTrailersNotInYard = this.$store.getters['vehicles/vehiclesByType']('Trailer').filter( (trailer: Vehicle) =>         
          !this.$store.getters['dailyYardVehicles/dailyYardVehicles'].find((vehicle1: DailyYardVehicle) => vehicle1.vehicle1Id ===  trailer.id) && 
          !this.$store.getters['dailyYardVehicles/dailyYardVehicles'].find((vehicle2: DailyYardVehicle) => vehicle2.vehicle2Id ===  trailer.id)
        );
        this.activeTrailersNotInYard = this.activeTrailersNotInYard.filter((item: Vehicle) => item.id !== this.selectedVehicle1.id);
      }
    }

    async addVehicle() {
      try {
        this.vehicleLoading = true;
        const response = await DailyYardVehicleService.create({
            vehicle1Id: this.selectedVehicle1.id,
            vehicle2Id: this.selectedVehicle2 && this.selectedVehicle2.id > 0 ? this.selectedVehicle2.id : undefined,
          });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);

            // reset drop-downs
            this.selectedVehicle1 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
            this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
            await this.listAll(true);
            this.updateActiveVechilesNotInYard();
            this.filterVehicles();
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Users.vue - addVehicle - 709 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.vehicleLoading = false;
      }
    }
  // #endregion

  // #region Functions required for the Update Status Dialog
    launchUpdateStatusDialog(item: DailyYardVehicle) {
      this.clickedItem = Object.assign({}, item);
      this.showUpdateStatusDialog = true;
    }

    async doUpdateStatus() {
      try {
        this.clicked = true;

        const response = await DailyYardVehicleService.updateStatus({
          id: this.clickedItem.id,
          statusId: this.clickedItem.statusId,
          comment: this.clickedItem.comment,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll(true);
            this.filterVehicles();
            this.showUpdateStatusDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DailyYardList.vue - doUpdateStatus - 753 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion
}
